import {ChevronDown} from "lucide-react";
import {useState} from "react";

import {Badge} from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {formatDate} from "@/src/modules/core/lib/formatters";
import {cn} from "@/lib/utils";

import {
  getSafelistingDayStatusBadgeVariant,
  getSafelistingDayStatusLabel,
  isPastCalendarDate,
} from "../lib/safelisting-quota-display";
import type {Safelisting} from "../types";

/** Match `SafelistingCoverageSummary` — collapse long day lists in detail modal. */
const DETAIL_COLLAPSE_THRESHOLD = 4;

type SafelistingDay = Safelisting["days"][number];

type SafelistingDaysStatusListProps = {
  days: Safelisting["days"];
  timeZone: string;
  className?: string;
  /** Entire permit excluded from household/plate quota. */
  excludedFromQuota?: boolean;
  /** Past days were corrected; no audit details in API. */
  accountabilityAdjusted?: boolean;
};

function PermitDayCards({days, timeZone}: {days: SafelistingDay[]; timeZone: string}) {
  return (
    <ul className="m-0 flex list-none flex-col gap-2 p-0" aria-label="Permit days">
      {days.map((day) => {
        const isPast = isPastCalendarDate(day.date, timeZone);
        const isCancelled = day.status === "cancelled";
        const isSkipped = day.status === "skipped";
        const quotaNote =
          isCancelled ? "Removed from quota" : isSkipped ? "Does not count" : null;

        return (
          <li
            key={day.safelisting_day_id ?? day.date}
            className={cn(
              "border-border flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2",
              isCancelled && "bg-muted/40",
            )}
          >
            <div className="flex min-w-0 flex-col gap-0.5">
              <span
                className={cn(
                  "text-sm font-medium tabular-nums",
                  isCancelled && "text-muted-foreground line-through",
                  isSkipped && "text-muted-foreground",
                )}
              >
                {formatDate(day.date)}
              </span>
              <span className="text-muted-foreground text-xs">
                {isPast ? "Past" : "Future"}
                {quotaNote ? ` · ${quotaNote}` : null}
              </span>
            </div>
            <Badge
              variant={getSafelistingDayStatusBadgeVariant(day.status)}
              className="shrink-0 px-1.5 py-0 text-[10px]"
            >
              {getSafelistingDayStatusLabel(day.status)}
            </Badge>
          </li>
        );
      })}
    </ul>
  );
}

export function SafelistingDaysStatusList({
  days,
  timeZone,
  className,
  excludedFromQuota = false,
  accountabilityAdjusted = false,
}: SafelistingDaysStatusListProps) {
  const sorted = [...(days ?? [])].sort((a, b) => a.date.localeCompare(b.date));
  const [detailsOpen, setDetailsOpen] = useState(sorted.length < DETAIL_COLLAPSE_THRESHOLD);
  const showCollapsibleDetails = sorted.length >= DETAIL_COLLAPSE_THRESHOLD;
  const contextNotes = [
    excludedFromQuota ? "Excluded from quota" : null,
    accountabilityAdjusted ? "Accountability adjusted" : null,
  ].filter((note): note is string => note != null);

  if (sorted.length === 0) {
    return (
      <div className={cn("flex flex-col gap-1.5", className)}>
        <span className="text-muted-foreground text-xs font-medium">Permit days</span>
        {contextNotes.length > 0 ? (
          <p className="text-muted-foreground text-xs leading-snug">{contextNotes.join(" · ")}</p>
        ) : null}
        <p className="text-muted-foreground text-sm">No permit days.</p>
      </div>
    );
  }

  const dayCountLabel = sorted.length === 1 ? "1 day" : `${sorted.length} days`;

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <div className="flex flex-col gap-0.5">
        <span className="text-muted-foreground text-xs font-medium">Permit days</span>
        {contextNotes.length > 0 ? (
          <p className="text-muted-foreground text-xs leading-snug">{contextNotes.join(" · ")}</p>
        ) : null}
      </div>

      {showCollapsibleDetails ? (
        <Collapsible open={detailsOpen} onOpenChange={setDetailsOpen}>
          <CollapsibleTrigger
            type="button"
            className="text-muted-foreground hover:text-foreground flex w-full items-center justify-between gap-2 text-left text-xs font-medium"
          >
            <span>{dayCountLabel}</span>
            <ChevronDown
              className={cn("size-3.5 shrink-0 transition-transform", detailsOpen && "rotate-180")}
              aria-hidden
            />
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-1.5">
            <PermitDayCards days={sorted} timeZone={timeZone} />
          </CollapsibleContent>
        </Collapsible>
      ) : (
        <PermitDayCards days={sorted} timeZone={timeZone} />
      )}
    </div>
  );
}
